import { useState } from 'react';
import { Box, Typography, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { useSupport } from '../../hooks/useSupport';
import type { SupportTicketStatus } from '../../types/support';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const STATUS_CONFIG: Record<SupportTicketStatus, { color: string; bg: string; border: string; label: string }> = {
  Open:                 { color: '#2563eb', bg: '#eff6ff',  border: '#bfdbfe', label: 'Open' },
  InProgress:           { color: '#d97706', bg: '#fffbeb',  border: '#fde68a', label: 'In Progress' },
  WaitingForCustomer:   { color: '#7c3aed', bg: '#f5f3ff',  border: '#ddd6fe', label: 'Waiting' },
  WaitingForThirdParty: { color: '#6b7280', bg: '#f9fafb',  border: '#e5e7eb', label: 'On Hold' },
  Resolved:             { color: '#16a34a', bg: '#f0fdf4',  border: '#bbf7d0', label: 'Resolved' },
  Closed:               { color: '#9ca3af', bg: '#f9fafb',  border: '#e5e7eb', label: 'Closed' },
  Reopened:             { color: '#dc2626', bg: '#fef2f2',  border: '#fecaca', label: 'Reopened' },
};

const PRIORITY_COLOR: Record<string, string> = {
  Critical: '#ef4444',
  High:     '#f97316',
  Normal:   '#8A70D6',
  Low:      '#9ca3af',
};

const FILTER_TABS: { label: string; value: SupportTicketStatus | 'all' }[] = [
  { label: 'All',      value: 'all' },
  { label: 'Open',     value: 'Open' },
  { label: 'Active',   value: 'InProgress' },
  { label: 'Resolved', value: 'Resolved' },
];

interface Props {
  onSelect: (id: number) => void;
}

export function TicketList({ onSelect }: Props) {
  const { useMyTickets } = useSupport();
  const { data, isLoading } = useMyTickets({ pageSize: 50 });
  const [filter, setFilter] = useState<SupportTicketStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  const tickets = data?.data ?? [];
  const filtered = tickets.filter((t) => {
    const matchesFilter = filter === 'all' || t.status === filter;
    const q = search.toLowerCase();
    const matchesSearch = !search || t.subject.toLowerCase().includes(q) || t.ticketNumber.toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{
            height: 70, borderRadius: '10px', bgcolor: '#f9fafb',
            animation: 'sPulse 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.12}s`,
            '@keyframes sPulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
          }} />
        ))}
      </Box>
    );
  }

  if (!tickets.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: '50%', bgcolor: '#f5f3ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 1.5, border: '1px solid #ddd6fe',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8A70D6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151', mb: 0.5 }}>
          No tickets yet
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
          Submit a request and we'll get back to you
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Search */}
      <TextField
        size="small"
        placeholder="Search tickets…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#f9fafb', borderRadius: '8px', fontSize: '0.78rem',
            '& fieldset': { borderColor: '#e5e7eb' },
            '&:hover fieldset': { borderColor: '#d1d5db' },
            '&.Mui-focused fieldset': { borderColor: '#8A70D6', borderWidth: 1.5 },
          },
          '& input::placeholder': { color: '#9ca3af', opacity: 1 },
        }}
      />

      {/* Filter pills */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {FILTER_TABS.map((f) => (
          <Box
            key={f.value}
            onClick={() => setFilter(f.value)}
            sx={{
              px: 1.125, py: 0.375, borderRadius: '20px', cursor: 'pointer',
              fontSize: '0.69rem', fontWeight: 600, transition: 'all 0.15s',
              ...(filter === f.value
                ? { bgcolor: '#8A70D6', color: '#fff', boxShadow: '0 2px 8px rgba(138,112,214,0.3)' }
                : { bgcolor: '#f3f4f6', color: '#6b7280', '&:hover': { bgcolor: '#e9eaec', color: '#374151' } }
              ),
            }}
          >
            {f.label}
          </Box>
        ))}
      </Box>

      {/* Ticket cards */}
      {filtered.length === 0 ? (
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', py: 3 }}>
          No tickets match
        </Typography>
      ) : (
        filtered.map((ticket) => {
          const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.Open;
          const dot = PRIORITY_COLOR[ticket.priority] ?? '#9ca3af';
          return (
            <Box
              key={ticket.id}
              onClick={() => onSelect(ticket.id)}
              sx={{
                p: 1.5, borderRadius: '10px', cursor: 'pointer',
                border: '1px solid #f0f0f0', bgcolor: '#fff',
                transition: 'all 0.15s ease', position: 'relative', overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                '&:hover': {
                  borderColor: '#ddd6fe',
                  boxShadow: '0 4px 12px rgba(138,112,214,0.1)',
                  transform: 'translateY(-1px)',
                },
                // priority bar
                '&::before': {
                  content: '""', position: 'absolute',
                  left: 0, top: '20%', bottom: '20%',
                  width: 3, borderRadius: '0 2px 2px 0', bgcolor: dot,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, pl: 1 }}>
                <Typography sx={{
                  fontWeight: 600, fontSize: '0.8rem', color: '#111827',
                  flex: 1, lineHeight: 1.4,
                  display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {ticket.subject}
                </Typography>
                <Box sx={{
                  px: 0.75, py: 0.2, borderRadius: '5px', flexShrink: 0,
                  bgcolor: cfg.bg, color: cfg.color,
                  fontSize: '0.6rem', fontWeight: 700,
                  border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap',
                }}>
                  {cfg.label}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, pl: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#9ca3af', letterSpacing: '0.03em' }}>
                  {ticket.ticketNumber}
                </Typography>
                {ticket.module && (
                  <>
                    <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: '#d1d5db' }} />
                    <Typography sx={{ fontSize: '0.62rem', color: '#9ca3af' }}>{ticket.module}</Typography>
                  </>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af' }}>
                  {dayjs(ticket.createdOn).fromNow()}
                </Typography>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
}
